import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, UserPlus, Search, ChevronDown, ChevronUp, Activity, FileText, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import FilterBar from '../components/FilterBar';
import PatientSummary from '../components/patient/PatientSummary';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const INITIAL_FORM_DATA = {
  first_name: '',
  last_name: '',
  dni: '',
  birth_date: '',
  gender: 'masculino',
  address: '',
  phone: '',
  email: '',
  insurance: '',
  doctor_id: '',
  grupo_sanguineo: '',
  estado_civil: '',
  // Parámetros clínicos
  peso: '',
  talla: '',
  imc: '',
  pa_sistolica: '',
  pa_diastolica: '',
  frecuencia_cardiaca: '',
  frecuencia_respiratoria: '',
  spo2: '',
  temperatura: '',
  // Antecedentes
  antecedentes_personales: '',
  antecedentes_quirurgicos: '',
  antecedentes_epidemiologicos: '',
  antecedentes_familiares_texto: '',
  alergias_texto: '',
  medicacion_habitual: '',
  habitos_nocivos: '',
};

const GRUPOS_SANGUINEOS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const ESTADOS_CIVILES = ['Soltero', 'Casado', 'Viudo', 'Divorciado', 'Conviviente'];

export default function PatientsManagement() {
  const { user, role } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Filtros adicionales del listado de pacientes
  const [patientFilters, setPatientFilters] = useState({ gender: 'todos', ageGroup: 'todos' });
  // Paciente cuya ficha clínica se está viendo
  const [summaryPatient, setSummaryPatient] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [myDoctors, setMyDoctors] = useState([]);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [showParametros, setShowParametros] = useState(false);
  const [showAntecedentes, setShowAntecedentes] = useState(false);

  // Estados de edición
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [editFormData, setEditFormData] = useState(INITIAL_FORM_DATA);
  const [editShowParametros, setEditShowParametros] = useState(false);
  const [editShowAntecedentes, setEditShowAntecedentes] = useState(false);

  // Estados de eliminación
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPatient, setDeletingPatient] = useState(null);
  /** El médico debe escribir el DNI del paciente para confirmar el borrado */
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const isNurse = role === 'nurse';

  // Cálculo automático del IMC
  const imcCalculado = useMemo(() => {
    const peso = parseFloat(formData.peso);
    const talla = parseFloat(formData.talla);
    if (peso > 0 && talla > 0) {
      const tallaM = talla / 100;
      return (peso / (tallaM * tallaM)).toFixed(2);
    }
    return '';
  }, [formData.peso, formData.talla]);

  // IMC calculado para edición
  const editImcCalculado = useMemo(() => {
    const peso = parseFloat(editFormData.peso);
    const talla = parseFloat(editFormData.talla);
    if (peso > 0 && talla > 0) {
      const tallaM = talla / 100;
      return (peso / (tallaM * tallaM)).toFixed(2);
    }
    return '';
  }, [editFormData.peso, editFormData.talla]);

  // El diálogo de nueva consulta enlaza aquí con ?nuevo=1 cuando el médico
  // necesita registrar a un paciente que aún no existe.
  useEffect(() => {
    if (searchParams.get('nuevo') === '1') {
      setDialogOpen(true);
      searchParams.delete('nuevo');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (user && role === 'nurse') {
      fetchMyDoctors();
    }
  }, [user, role]);

  const fetchMyDoctors = async () => {
    try {
      const response = await api.get('/nurses/my-doctors');
      setMyDoctors(response.data);
      if (response.data.length === 0) {
        toast.warning('No tiene médicos asignados. Contacte al administrador para ser asignada a un médico.');
      }
    } catch (error) {
      toast.error('Error al cargar los médicos asignados');
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data);
    } catch (error) {
      toast.error('Error al cargar pacientes');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isNurse && !formData.doctor_id) {
      toast.error('Debe seleccionar el médico tratante');
      return;
    }

    try {
      const submitData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        dni: formData.dni,
        birth_date: formData.birth_date,
        gender: formData.gender,
        address: formData.address || null,
        phone: formData.phone || null,
        email: formData.email || null,
        insurance: formData.insurance || null,
        grupo_sanguineo: formData.grupo_sanguineo || null,
        estado_civil: formData.estado_civil || null,
        antecedentes_personales: formData.antecedentes_personales || null,
        antecedentes_quirurgicos: formData.antecedentes_quirurgicos || null,
        antecedentes_epidemiologicos: formData.antecedentes_epidemiologicos || null,
        antecedentes_familiares_texto: formData.antecedentes_familiares_texto || null,
        alergias_texto: formData.alergias_texto || null,
        medicacion_habitual: formData.medicacion_habitual || null,
        habitos_nocivos: formData.habitos_nocivos || null,
      };

      if (isNurse) {
        submitData.doctor_id = formData.doctor_id;
      }

      // Construir parámetros clínicos solo si al menos uno tiene valor
      const paramFields = ['peso', 'talla', 'pa_sistolica', 'pa_diastolica',
        'frecuencia_cardiaca', 'frecuencia_respiratoria', 'spo2', 'temperatura'];
      const hasAnyParam = paramFields.some((f) => formData[f] !== '' && formData[f] !== null);

      if (hasAnyParam) {
        const parametros = {};
        if (formData.peso) parametros.peso = parseFloat(formData.peso);
        if (formData.talla) parametros.talla = parseFloat(formData.talla);
        if (imcCalculado) parametros.imc = parseFloat(imcCalculado);
        if (formData.pa_sistolica) parametros.pa_sistolica = parseInt(formData.pa_sistolica);
        if (formData.pa_diastolica) parametros.pa_diastolica = parseInt(formData.pa_diastolica);
        if (formData.frecuencia_cardiaca) parametros.frecuencia_cardiaca = parseInt(formData.frecuencia_cardiaca);
        if (formData.frecuencia_respiratoria) parametros.frecuencia_respiratoria = parseInt(formData.frecuencia_respiratoria);
        if (formData.spo2) parametros.spo2 = parseInt(formData.spo2);
        if (formData.temperatura) parametros.temperatura = parseFloat(formData.temperatura);
        submitData.parametros_clinicos = parametros;
      }

      await api.post('/patients', submitData);
      toast.success('Paciente registrado exitosamente');
      setDialogOpen(false);
      setFormData(INITIAL_FORM_DATA);
      setShowParametros(false);
      setShowAntecedentes(false);
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar paciente');
    }
  };

  const handleEditPatient = (patient) => {
    setEditingPatient(patient);
    setEditFormData({
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      dni: patient.dni || '',
      birth_date: patient.birth_date ? patient.birth_date.split('T')[0] : '',
      gender: patient.gender || 'masculino',
      address: patient.address || '',
      phone: patient.phone || '',
      email: patient.email || '',
      insurance: patient.insurance || '',
      doctor_id: '',
      grupo_sanguineo: patient.grupo_sanguineo || '',
      estado_civil: patient.estado_civil || '',
      peso: '',
      talla: '',
      imc: '',
      pa_sistolica: '',
      pa_diastolica: '',
      frecuencia_cardiaca: '',
      frecuencia_respiratoria: '',
      spo2: '',
      temperatura: '',
      antecedentes_personales: patient.antecedentes_personales || '',
      antecedentes_quirurgicos: patient.antecedentes_quirurgicos || '',
      antecedentes_epidemiologicos: patient.antecedentes_epidemiologicos || '',
      antecedentes_familiares_texto: patient.antecedentes_familiares_texto || '',
      alergias_texto: patient.alergias_texto || '',
      medicacion_habitual: patient.medicacion_habitual || '',
      habitos_nocivos: patient.habitos_nocivos || '',
    });
    const hasAntecedentes = patient.antecedentes_personales || patient.antecedentes_quirurgicos ||
      patient.antecedentes_epidemiologicos || patient.antecedentes_familiares_texto || patient.alergias_texto ||
      patient.medicacion_habitual || patient.habitos_nocivos;
    setEditShowAntecedentes(!!hasAntecedentes);
    setEditShowParametros(false);
    setEditDialogOpen(true);
  };

  const handleUpdatePatient = async (e) => {
    e.preventDefault();
    if (!editingPatient) return;

    try {
      const updateData = {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        dni: editFormData.dni,
        birth_date: editFormData.birth_date,
        gender: editFormData.gender,
        address: editFormData.address || null,
        phone: editFormData.phone || null,
        email: editFormData.email || null,
        insurance: editFormData.insurance || null,
        grupo_sanguineo: editFormData.grupo_sanguineo || null,
        estado_civil: editFormData.estado_civil || null,
        antecedentes_personales: editFormData.antecedentes_personales || null,
        antecedentes_quirurgicos: editFormData.antecedentes_quirurgicos || null,
        antecedentes_epidemiologicos: editFormData.antecedentes_epidemiologicos || null,
        antecedentes_familiares_texto: editFormData.antecedentes_familiares_texto || null,
        alergias_texto: editFormData.alergias_texto || null,
        medicacion_habitual: editFormData.medicacion_habitual || null,
        habitos_nocivos: editFormData.habitos_nocivos || null,
      };

      await api.put(`/patients/${editingPatient.id}`, updateData);
      toast.success('Paciente actualizado exitosamente');
      setEditDialogOpen(false);
      setEditingPatient(null);
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar paciente');
    }
  };

  const handleEditChange = (field) => (e) => {
    setEditFormData({ ...editFormData, [field]: e.target.value });
  };

  /**
   * Abre el diálogo de confirmación de borrado.
   * El texto de confirmación se reinicia para que no quede validado de un borrado anterior.
   */
  const handleOpenDeleteDialog = (patient) => {
    setDeletingPatient(patient);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  /**
   * Elimina el paciente y toda su información asociada.
   * El backend borra en cascada consultas, triajes, citas, historia médica y alertas.
   */
  const handleDeletePatient = async () => {
    if (!deletingPatient) return;

    setDeleting(true);
    try {
      await api.delete(`/patients/${deletingPatient.id}`);
      toast.success('Paciente eliminado correctamente');
      setDeleteDialogOpen(false);
      setDeletingPatient(null);
      setDeleteConfirmText('');
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar paciente');
    } finally {
      setDeleting(false);
    }
  };

  const getImcCategory = (imc) => {
    const val = parseFloat(imc);
    if (!val) return null;
    if (val < 18.5) return { label: 'Bajo peso', color: 'text-blue-600' };
    if (val < 25) return { label: 'Normal', color: 'text-green-600' };
    if (val < 30) return { label: 'Sobrepeso', color: 'text-yellow-600' };
    return { label: 'Obesidad', color: 'text-red-600' };
  };

  /** Edad en años cumplidos, o null si no hay fecha de nacimiento */
  const getAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    if (isNaN(birth)) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
    return age;
  };

  /** Grupos etarios con sentido clínico; deben coincidir con los del panel */
  const AGE_GROUPS = {
    '0-11': [0, 11], '12-17': [12, 17], '18-39': [18, 39],
    '40-59': [40, 59], '60-74': [60, 74], '75+': [75, 200],
  };

  const filteredPatients = patients.filter((patient) => {
    if (patientFilters.gender !== 'todos' && patient.gender !== patientFilters.gender) {
      return false;
    }

    if (patientFilters.ageGroup !== 'todos') {
      const age = getAge(patient.birth_date);
      const [min, max] = AGE_GROUPS[patientFilters.ageGroup] || [];
      if (age === null || age < min || age > max) return false;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    if (!searchLower) return true;

    return (
      patient.first_name.toLowerCase().includes(searchLower) ||
      patient.last_name.toLowerCase().includes(searchLower) ||
      `${patient.first_name} ${patient.last_name}`.toLowerCase().includes(searchLower) ||
      patient.dni.includes(searchLower) ||
      patient.internal_id.toLowerCase().includes(searchLower) ||
      (patient.phone || '').includes(searchLower)
    );
  });

  const imcCat = getImcCategory(imcCalculado);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="patients-title">
            Mis Pacientes
          </h1>
          <p className="text-muted-foreground mt-2">Gestiona tus pacientes registrados</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary" data-testid="create-patient-button">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Paciente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-patient-dialog">
            <DialogHeader>
              <DialogTitle>Registrar Nuevo Paciente</DialogTitle>
              <DialogDescription>
                Complete el registro del paciente. Los campos con * son obligatorios.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* ===== DATOS PERSONALES ===== */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Nombres *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={handleChange('first_name')}
                    required
                    data-testid="patient-first-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Apellidos *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={handleChange('last_name')}
                    required
                    data-testid="patient-last-name-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dni">DNI / Documento *</Label>
                  <Input
                    id="dni"
                    value={formData.dni}
                    onChange={handleChange('dni')}
                    required
                    data-testid="patient-dni-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birth_date">Fecha de Nacimiento *</Label>
                  <Input
                    id="birth_date"
                    type="date"
                    value={formData.birth_date}
                    onChange={handleChange('birth_date')}
                    required
                    data-testid="patient-birth-date-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gender">Sexo *</Label>
                  <select
                    id="gender"
                    value={formData.gender}
                    onChange={handleChange('gender')}
                    className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="patient-gender-select"
                  >
                    <option value="masculino">Masculino</option>
                    <option value="femenino">Femenino</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange('phone')}
                    data-testid="patient-phone-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="grupo_sanguineo">Grupo Sanguíneo</Label>
                  <select
                    id="grupo_sanguineo"
                    value={formData.grupo_sanguineo}
                    onChange={handleChange('grupo_sanguineo')}
                    className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Seleccionar</option>
                    {GRUPOS_SANGUINEOS.map((gs) => (
                      <option key={gs} value={gs}>{gs}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estado_civil">Estado Civil</Label>
                  <select
                    id="estado_civil"
                    value={formData.estado_civil}
                    onChange={handleChange('estado_civil')}
                    className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Seleccionar</option>
                    {ESTADOS_CIVILES.map((ec) => (
                      <option key={ec} value={ec}>{ec}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Correo Electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  data-testid="patient-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={handleChange('address')}
                  data-testid="patient-address-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="insurance">Seguro</Label>
                <Input
                  id="insurance"
                  value={formData.insurance}
                  onChange={handleChange('insurance')}
                  placeholder="Ej: EsSalud, SIS, Particular"
                  data-testid="patient-insurance-input"
                />
              </div>

              {/* Doctor selection - only for nurses */}
              {(isNurse || myDoctors.length > 0) && (
                <div className="space-y-2">
                  <Label htmlFor="doctor_id">Médico Tratante *</Label>
                  <select
                    id="doctor_id"
                    value={formData.doctor_id}
                    onChange={handleChange('doctor_id')}
                    className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="patient-doctor-select"
                  >
                    <option value="">Seleccione un médico</option>
                    {myDoctors.map((doctor) => (
                      <option key={doctor.user_id} value={doctor.user_id}>
                        {doctor.full_name} - {doctor.specialization}
                      </option>
                    ))}
                  </select>
                  {myDoctors.length === 0 && (
                    <p className="text-xs text-amber-600">
                      No tiene médicos asignados. Contacte al administrador.
                    </p>
                  )}
                </div>
              )}

              {/* ===== PARÁMETROS CLÍNICOS (COLAPSABLE) ===== */}
              <div className="border border-input rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowParametros(!showParametros)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted/80 transition-colors"
                >
                  <span className="flex items-center gap-2 font-medium text-sm">
                    <Activity className="w-4 h-4 text-primary" />
                    Parámetros Clínicos (Opcional)
                  </span>
                  {showParametros ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showParametros && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="peso">Peso (kg)</Label>
                        <Input
                          id="peso"
                          type="number"
                          step="0.1"
                          min="0"
                          max="500"
                          value={formData.peso}
                          onChange={handleChange('peso')}
                          placeholder="Ej: 70.5"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="talla">Talla (cm)</Label>
                        <Input
                          id="talla"
                          type="number"
                          step="0.1"
                          min="0"
                          max="300"
                          value={formData.talla}
                          onChange={handleChange('talla')}
                          placeholder="Ej: 165"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="imc">IMC (calculado)</Label>
                        <Input
                          id="imc"
                          type="text"
                          value={imcCalculado}
                          disabled
                          className="bg-muted"
                        />
                        {imcCat && (
                          <p className={`text-xs font-medium ${imcCat.color}`}>{imcCat.label}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="pa_sistolica">PA Sistólica (mmHg)</Label>
                        <Input
                          id="pa_sistolica"
                          type="number"
                          min="0"
                          max="300"
                          value={formData.pa_sistolica}
                          onChange={handleChange('pa_sistolica')}
                          placeholder="Ej: 120"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pa_diastolica">PA Diastólica (mmHg)</Label>
                        <Input
                          id="pa_diastolica"
                          type="number"
                          min="0"
                          max="200"
                          value={formData.pa_diastolica}
                          onChange={handleChange('pa_diastolica')}
                          placeholder="Ej: 80"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="frecuencia_cardiaca">FC (lpm)</Label>
                        <Input
                          id="frecuencia_cardiaca"
                          type="number"
                          min="0"
                          max="300"
                          value={formData.frecuencia_cardiaca}
                          onChange={handleChange('frecuencia_cardiaca')}
                          placeholder="Ej: 72"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="frecuencia_respiratoria">FR (rpm)</Label>
                        <Input
                          id="frecuencia_respiratoria"
                          type="number"
                          min="0"
                          max="100"
                          value={formData.frecuencia_respiratoria}
                          onChange={handleChange('frecuencia_respiratoria')}
                          placeholder="Ej: 16"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="spo2">SpO2 (%)</Label>
                        <Input
                          id="spo2"
                          type="number"
                          min="0"
                          max="100"
                          value={formData.spo2}
                          onChange={handleChange('spo2')}
                          placeholder="Ej: 98"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="temperatura">Temperatura (°C)</Label>
                        <Input
                          id="temperatura"
                          type="number"
                          step="0.1"
                          min="30"
                          max="45"
                          value={formData.temperatura}
                          onChange={handleChange('temperatura')}
                          placeholder="Ej: 36.5"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ===== ANTECEDENTES (COLAPSABLE) ===== */}
              <div className="border border-input rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowAntecedentes(!showAntecedentes)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted/80 transition-colors"
                >
                  <span className="flex items-center gap-2 font-medium text-sm">
                    <FileText className="w-4 h-4 text-primary" />
                    Antecedentes (Opcional)
                  </span>
                  {showAntecedentes ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showAntecedentes && (
                  <div className="p-4 space-y-4">
                    {/* Antecedentes Personales */}
                    <div className="space-y-2">
                      <Label htmlFor="antecedentes_personales">Antecedentes Personales</Label>
                      <div className="flex flex-wrap gap-1.5 mb-1">
                        {['Diabetes tipo 2', 'HTA', 'Asma', 'Hipotiroidismo', 'Obesidad', 'Dislipidemia', 'Gastritis', 'Artrosis', 'EPOC', 'IRC'].map((item) => (
                          <button key={item} type="button"
                            className="text-xs px-2 py-1 rounded-full border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
                            onClick={() => {
                              const current = formData.antecedentes_personales;
                              const newVal = current ? `${current}, ${item}` : item;
                              setFormData({...formData, antecedentes_personales: newVal});
                            }}>+ {item}</button>
                        ))}
                      </div>
                      <textarea
                        id="antecedentes_personales"
                        value={formData.antecedentes_personales}
                        onChange={handleChange('antecedentes_personales')}
                        className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                        placeholder="Ej: Diabetes tipo 2 diagnosticada en 2020, Hipertensión arterial..."
                      />
                    </div>

                    {/* Antecedentes Quirúrgicos */}
                    <div className="space-y-2">
                      <Label htmlFor="antecedentes_quirurgicos">Antecedentes Quirúrgicos</Label>
                      <div className="flex flex-wrap gap-1.5 mb-1">
                        {['Apendicectomía', 'Colecistectomía', 'Cesárea', 'Hernioplastía', 'Histerectomía', 'Amigdalectomía', 'Artroscopia'].map((item) => (
                          <button key={item} type="button"
                            className="text-xs px-2 py-1 rounded-full border border-orange-200 text-orange-600 hover:bg-orange-50 transition-colors"
                            onClick={() => {
                              const current = formData.antecedentes_quirurgicos;
                              const newVal = current ? `${current}, ${item}` : item;
                              setFormData({...formData, antecedentes_quirurgicos: newVal});
                            }}>+ {item}</button>
                        ))}
                      </div>
                      <textarea
                        id="antecedentes_quirurgicos"
                        value={formData.antecedentes_quirurgicos}
                        onChange={handleChange('antecedentes_quirurgicos')}
                        className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                        placeholder="Ej: Apendicectomía 2015, Colecistectomía 2018..."
                      />
                    </div>

                    {/* Antecedentes Epidemiológicos */}
                    <div className="space-y-2">
                      <Label htmlFor="antecedentes_epidemiologicos">Antecedentes Epidemiológicos</Label>
                      <textarea
                        id="antecedentes_epidemiologicos"
                        value={formData.antecedentes_epidemiologicos}
                        onChange={handleChange('antecedentes_epidemiologicos')}
                        className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                        placeholder="Ej: Viaje reciente a zona endémica, contacto con caso de TBC..."
                      />
                    </div>

                    {/* Antecedentes Familiares */}
                    <div className="space-y-2">
                      <Label htmlFor="antecedentes_familiares_texto">Antecedentes Familiares</Label>
                      <div className="flex flex-wrap gap-1.5 mb-1">
                        {['HTA (padre)', 'HTA (madre)', 'DM (padre)', 'DM (madre)', 'Cáncer', 'ACV', 'Cardiopatía', 'Asma'].map((item) => (
                          <button key={item} type="button"
                            className="text-xs px-2 py-1 rounded-full border border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors"
                            onClick={() => {
                              const current = formData.antecedentes_familiares_texto;
                              const newVal = current ? `${current}, ${item}` : item;
                              setFormData({...formData, antecedentes_familiares_texto: newVal});
                            }}>+ {item}</button>
                        ))}
                      </div>
                      <textarea
                        id="antecedentes_familiares_texto"
                        value={formData.antecedentes_familiares_texto}
                        onChange={handleChange('antecedentes_familiares_texto')}
                        className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                        placeholder="Ej: Padre con HTA y DM, Madre con cáncer de mama..."
                      />
                    </div>

                    {/* Alergias */}
                    <div className="space-y-2">
                      <Label htmlFor="alergias_texto">Alergias</Label>
                      <div className="flex flex-wrap gap-1.5 mb-1">
                        {['Penicilina', 'Sulfas', 'AINEs', 'Látex', 'Mariscos', 'NIEGA alergias'].map((item) => (
                          <button key={item} type="button"
                            className={`text-xs px-2 py-1 rounded-full border transition-colors ${item === 'NIEGA alergias' ? 'border-green-200 text-green-600 hover:bg-green-50 font-medium' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
                            onClick={() => {
                              if (item === 'NIEGA alergias') {
                                setFormData({...formData, alergias_texto: 'Niega alergias'});
                              } else {
                                const current = formData.alergias_texto;
                                const newVal = current && current !== 'Niega alergias' ? `${current}, ${item}` : item;
                                setFormData({...formData, alergias_texto: newVal});
                              }
                            }}>+ {item}</button>
                        ))}
                      </div>
                      <textarea
                        id="alergias_texto"
                        value={formData.alergias_texto}
                        onChange={handleChange('alergias_texto')}
                        className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                        placeholder="Ej: Penicilina (rash cutáneo), Mariscos (anafilaxia)..."
                      />
                    </div>

                    {/* Medicación Habitual */}
                    <div className="space-y-2">
                      <Label htmlFor="medicacion_habitual">Medicación Habitual</Label>
                      <div className="flex flex-wrap gap-1.5 mb-1">
                        {['Metformina', 'Losartán', 'Enalapril', 'Omeprazol', 'Atorvastatina', 'Levotiroxina', 'Aspirina', 'Amlodipino'].map((item) => (
                          <button key={item} type="button"
                            className="text-xs px-2 py-1 rounded-full border border-cyan-200 text-cyan-600 hover:bg-cyan-50 transition-colors"
                            onClick={() => {
                              const current = formData.medicacion_habitual;
                              const newVal = current ? `${current}, ${item}` : item;
                              setFormData({...formData, medicacion_habitual: newVal});
                            }}>+ {item}</button>
                        ))}
                      </div>
                      <textarea
                        id="medicacion_habitual"
                        value={formData.medicacion_habitual}
                        onChange={handleChange('medicacion_habitual')}
                        className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                        placeholder="Ej: Metformina 850mg c/12h, Losartán 50mg c/24h..."
                      />
                    </div>

                    {/* Hábitos Nocivos */}
                    <div className="space-y-2">
                      <Label htmlFor="habitos_nocivos">Hábitos Nocivos</Label>
                      <div className="flex flex-wrap gap-1.5 mb-1">
                        {['Tabaco', 'Alcohol', 'Drogas recreativas', 'Sedentarismo', 'NIEGA hábitos nocivos'].map((item) => (
                          <button key={item} type="button"
                            className={`text-xs px-2 py-1 rounded-full border transition-colors ${item === 'NIEGA hábitos nocivos' ? 'border-green-200 text-green-600 hover:bg-green-50 font-medium' : 'border-amber-200 text-amber-600 hover:bg-amber-50'}`}
                            onClick={() => {
                              if (item === 'NIEGA hábitos nocivos') {
                                setFormData({...formData, habitos_nocivos: 'Niega hábitos nocivos'});
                              } else {
                                const current = formData.habitos_nocivos;
                                const newVal = current && current !== 'Niega hábitos nocivos' ? `${current}, ${item}` : item;
                                setFormData({...formData, habitos_nocivos: newVal});
                              }
                            }}>+ {item}</button>
                        ))}
                      </div>
                      <textarea
                        id="habitos_nocivos"
                        value={formData.habitos_nocivos}
                        onChange={handleChange('habitos_nocivos')}
                        className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                        placeholder="Ej: Tabaco (10 cigarros/día desde hace 5 años), Alcohol social..."
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-patient-button">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-primary" data-testid="submit-patient-button">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Registrar Paciente
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Búsqueda y filtros */}
      <FilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Buscar por nombre, DNI, ID interno o teléfono..."
        filters={[
          {
            key: 'gender',
            label: 'Sexo',
            value: patientFilters.gender,
            options: [
              { value: 'femenino', label: 'Femenino' },
              { value: 'masculino', label: 'Masculino' },
              { value: 'otro', label: 'Otro' },
            ],
            onChange: (value) => setPatientFilters(f => ({ ...f, gender: value })),
          },
          {
            key: 'age',
            label: 'Edad',
            value: patientFilters.ageGroup,
            options: Object.keys(AGE_GROUPS).map(group => ({ value: group, label: `${group} años` })),
            onChange: (value) => setPatientFilters(f => ({ ...f, ageGroup: value })),
          },
        ]}
        resultCount={filteredPatients.length}
        totalCount={patients.length}
        onClear={() => { setSearchTerm(''); setPatientFilters({ gender: 'todos', ageGroup: 'todos' }); }}
      />

      <Card className="p-6" data-testid="patients-table-card">
        {loading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : filteredPatients.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchTerm ? 'No se encontraron pacientes' : 'No hay pacientes registrados'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID Interno</TableHead>
                <TableHead>Nombre Completo</TableHead>
                <TableHead>DNI</TableHead>
                <TableHead>Edad</TableHead>
                <TableHead>Grupo Sang.</TableHead>
                <TableHead>Estado Civil</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Seguro</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPatients.map((patient) => (
                <TableRow key={patient.id} data-testid={`patient-row-${patient.id}`}>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {patient.internal_id}
                    </code>
                  </TableCell>
                  <TableCell className="font-medium">
                    <button
                      type="button"
                      onClick={() => setSummaryPatient(patient)}
                      className="text-blue-700 hover:underline text-left"
                      title="Ver historial clínico"
                    >
                      {patient.first_name} {patient.last_name}
                    </button>
                  </TableCell>
                  <TableCell>{patient.dni}</TableCell>
                  <TableCell>
                    {new Date().getFullYear() -
                      new Date(patient.birth_date).getFullYear()}{' '}
                    años
                  </TableCell>
                  <TableCell>
                    {patient.grupo_sanguineo ? (
                      <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800 font-medium">
                        {patient.grupo_sanguineo}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{patient.estado_civil || '-'}</TableCell>
                  <TableCell>{patient.phone || '-'}</TableCell>
                  <TableCell>
                    <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                      {patient.insurance || 'Particular'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditPatient(patient)}>
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDeleteDialog(patient)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        title="Eliminar paciente"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* ===== DIALOG DE EDICIÓN DE PACIENTE ===== */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Paciente</DialogTitle>
            <DialogDescription>
              Modifique los datos del paciente. Los campos con * son obligatorios.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePatient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombres *</Label>
                <Input value={editFormData.first_name} onChange={handleEditChange('first_name')} required />
              </div>
              <div className="space-y-2">
                <Label>Apellidos *</Label>
                <Input value={editFormData.last_name} onChange={handleEditChange('last_name')} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>DNI / Documento *</Label>
                <Input value={editFormData.dni} onChange={handleEditChange('dni')} required />
              </div>
              <div className="space-y-2">
                <Label>Fecha de Nacimiento *</Label>
                <Input type="date" value={editFormData.birth_date} onChange={handleEditChange('birth_date')} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sexo *</Label>
                <select value={editFormData.gender} onChange={handleEditChange('gender')}
                  className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20" required>
                  <option value="masculino">Masculino</option>
                  <option value="femenino">Femenino</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={editFormData.phone} onChange={handleEditChange('phone')} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Grupo Sanguíneo</Label>
                <select value={editFormData.grupo_sanguineo} onChange={handleEditChange('grupo_sanguineo')}
                  className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20">
                  <option value="">Seleccionar</option>
                  {GRUPOS_SANGUINEOS.map((gs) => <option key={gs} value={gs}>{gs}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Estado Civil</Label>
                <select value={editFormData.estado_civil} onChange={handleEditChange('estado_civil')}
                  className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20">
                  <option value="">Seleccionar</option>
                  {ESTADOS_CIVILES.map((ec) => <option key={ec} value={ec}>{ec}</option>)}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Correo Electrónico</Label>
              <Input type="email" value={editFormData.email} onChange={handleEditChange('email')} />
            </div>

            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input value={editFormData.address} onChange={handleEditChange('address')} />
            </div>

            <div className="space-y-2">
              <Label>Seguro</Label>
              <Input value={editFormData.insurance} onChange={handleEditChange('insurance')} placeholder="Ej: EsSalud, SIS, Particular" />
            </div>

            {/* Antecedentes en edición */}
            <div className="border border-input rounded-lg overflow-hidden">
              <button type="button" onClick={() => setEditShowAntecedentes(!editShowAntecedentes)}
                className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted/80 transition-colors">
                <span className="flex items-center gap-2 font-medium text-sm">
                  <FileText className="w-4 h-4 text-primary" />
                  Antecedentes
                </span>
                {editShowAntecedentes ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {editShowAntecedentes && (
                <div className="p-4 space-y-4">
                  {/* Antecedentes Personales */}
                  <div className="space-y-2">
                    <Label>Antecedentes Personales</Label>
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {['Diabetes tipo 2', 'HTA', 'Asma', 'Hipotiroidismo', 'Obesidad', 'Dislipidemia', 'Gastritis', 'Artrosis', 'EPOC', 'IRC'].map((item) => (
                        <button key={item} type="button"
                          className="text-xs px-2 py-1 rounded-full border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
                          onClick={() => {
                            const current = editFormData.antecedentes_personales;
                            const newVal = current ? `${current}, ${item}` : item;
                            setEditFormData({...editFormData, antecedentes_personales: newVal});
                          }}>+ {item}</button>
                      ))}
                    </div>
                    <textarea value={editFormData.antecedentes_personales} onChange={handleEditChange('antecedentes_personales')}
                      className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                      placeholder="Ej: Diabetes tipo 2, Hipertensión arterial..." />
                  </div>

                  {/* Antecedentes Quirúrgicos */}
                  <div className="space-y-2">
                    <Label>Antecedentes Quirúrgicos</Label>
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {['Apendicectomía', 'Colecistectomía', 'Cesárea', 'Hernioplastía', 'Histerectomía', 'Amigdalectomía', 'Artroscopia'].map((item) => (
                        <button key={item} type="button"
                          className="text-xs px-2 py-1 rounded-full border border-orange-200 text-orange-600 hover:bg-orange-50 transition-colors"
                          onClick={() => {
                            const current = editFormData.antecedentes_quirurgicos;
                            const newVal = current ? `${current}, ${item}` : item;
                            setEditFormData({...editFormData, antecedentes_quirurgicos: newVal});
                          }}>+ {item}</button>
                      ))}
                    </div>
                    <textarea value={editFormData.antecedentes_quirurgicos} onChange={handleEditChange('antecedentes_quirurgicos')}
                      className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                      placeholder="Ej: Apendicectomía 2015..." />
                  </div>

                  {/* Antecedentes Epidemiológicos */}
                  <div className="space-y-2">
                    <Label>Antecedentes Epidemiológicos</Label>
                    <textarea value={editFormData.antecedentes_epidemiologicos} onChange={handleEditChange('antecedentes_epidemiologicos')}
                      className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                      placeholder="Ej: Viaje a zona endémica..." />
                  </div>

                  {/* Antecedentes Familiares */}
                  <div className="space-y-2">
                    <Label>Antecedentes Familiares</Label>
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {['HTA (padre)', 'HTA (madre)', 'DM (padre)', 'DM (madre)', 'Cáncer', 'ACV', 'Cardiopatía', 'Asma'].map((item) => (
                        <button key={item} type="button"
                          className="text-xs px-2 py-1 rounded-full border border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors"
                          onClick={() => {
                            const current = editFormData.antecedentes_familiares_texto;
                            const newVal = current ? `${current}, ${item}` : item;
                            setEditFormData({...editFormData, antecedentes_familiares_texto: newVal});
                          }}>+ {item}</button>
                      ))}
                    </div>
                    <textarea value={editFormData.antecedentes_familiares_texto} onChange={handleEditChange('antecedentes_familiares_texto')}
                      className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                      placeholder="Ej: Padre con HTA y DM..." />
                  </div>

                  {/* Alergias */}
                  <div className="space-y-2">
                    <Label>Alergias</Label>
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {['Penicilina', 'Sulfas', 'AINEs', 'Látex', 'Mariscos', 'NIEGA alergias'].map((item) => (
                        <button key={item} type="button"
                          className={`text-xs px-2 py-1 rounded-full border transition-colors ${item === 'NIEGA alergias' ? 'border-green-200 text-green-600 hover:bg-green-50 font-medium' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
                          onClick={() => {
                            if (item === 'NIEGA alergias') {
                              setEditFormData({...editFormData, alergias_texto: 'Niega alergias'});
                            } else {
                              const current = editFormData.alergias_texto;
                              const newVal = current && current !== 'Niega alergias' ? `${current}, ${item}` : item;
                              setEditFormData({...editFormData, alergias_texto: newVal});
                            }
                          }}>+ {item}</button>
                      ))}
                    </div>
                    <textarea value={editFormData.alergias_texto} onChange={handleEditChange('alergias_texto')}
                      className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                      placeholder="Ej: Penicilina (rash cutáneo)..." />
                  </div>

                  {/* Medicación Habitual */}
                  <div className="space-y-2">
                    <Label>Medicación Habitual</Label>
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {['Metformina', 'Losartán', 'Enalapril', 'Omeprazol', 'Atorvastatina', 'Levotiroxina', 'Aspirina', 'Amlodipino'].map((item) => (
                        <button key={item} type="button"
                          className="text-xs px-2 py-1 rounded-full border border-cyan-200 text-cyan-600 hover:bg-cyan-50 transition-colors"
                          onClick={() => {
                            const current = editFormData.medicacion_habitual;
                            const newVal = current ? `${current}, ${item}` : item;
                            setEditFormData({...editFormData, medicacion_habitual: newVal});
                          }}>+ {item}</button>
                      ))}
                    </div>
                    <textarea value={editFormData.medicacion_habitual} onChange={handleEditChange('medicacion_habitual')}
                      className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                      placeholder="Ej: Metformina 850mg c/12h, Losartán 50mg c/24h..." />
                  </div>

                  {/* Hábitos Nocivos */}
                  <div className="space-y-2">
                    <Label>Hábitos Nocivos</Label>
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {['Tabaco', 'Alcohol', 'Drogas recreativas', 'Sedentarismo', 'NIEGA hábitos nocivos'].map((item) => (
                        <button key={item} type="button"
                          className={`text-xs px-2 py-1 rounded-full border transition-colors ${item === 'NIEGA hábitos nocivos' ? 'border-green-200 text-green-600 hover:bg-green-50 font-medium' : 'border-amber-200 text-amber-600 hover:bg-amber-50'}`}
                          onClick={() => {
                            if (item === 'NIEGA hábitos nocivos') {
                              setEditFormData({...editFormData, habitos_nocivos: 'Niega hábitos nocivos'});
                            } else {
                              const current = editFormData.habitos_nocivos;
                              const newVal = current && current !== 'Niega hábitos nocivos' ? `${current}, ${item}` : item;
                              setEditFormData({...editFormData, habitos_nocivos: newVal});
                            }
                          }}>+ {item}</button>
                      ))}
                    </div>
                    <textarea value={editFormData.habitos_nocivos} onChange={handleEditChange('habitos_nocivos')}
                      className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary/20 min-h-[60px] resize-y text-sm"
                      placeholder="Ej: Tabaco (10 cigarros/día desde hace 5 años), Alcohol social..." />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-primary">
                <Pencil className="w-4 h-4 mr-2" />
                Guardar Cambios
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== DIALOG DE ELIMINACIÓN DE PACIENTE ===== */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Eliminar paciente
            </DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>

          {deletingPatient && (
            <div className="space-y-4">
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="font-semibold text-red-900">
                  {deletingPatient.first_name} {deletingPatient.last_name}
                </p>
                <p className="text-sm text-red-800">DNI: {deletingPatient.dni}</p>
                <p className="mt-3 text-sm text-red-800">
                  Se eliminarán de forma permanente sus <strong>consultas</strong>,{' '}
                  <strong>triajes</strong>, <strong>citas</strong>,{' '}
                  <strong>historia médica</strong>, <strong>alertas</strong> y su{' '}
                  <strong>cuenta de acceso</strong>.
                </p>
              </div>

              <div>
                <Label>
                  Para confirmar, escriba el DNI del paciente:{' '}
                  <span className="font-mono font-semibold">{deletingPatient.dni}</span>
                </Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Escriba el DNI aquí"
                  className="mt-1"
                  autoComplete="off"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleDeletePatient}
                  disabled={deleteConfirmText.trim() !== deletingPatient.dni || deleting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleting ? 'Eliminando...' : 'Eliminar definitivamente'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ficha clínica: el mismo resumen que ve el médico al abrir una consulta,
          en versión completa. Se abre al pulsar el nombre del paciente. */}
      <Dialog open={Boolean(summaryPatient)} onOpenChange={(open) => !open && setSummaryPatient(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historial clínico</DialogTitle>
            <DialogDescription>
              Resumen del paciente antes de la atención: alertas, consultas previas y
              medicación ya recetada.
            </DialogDescription>
          </DialogHeader>
          {summaryPatient && (
            <PatientSummary patientId={summaryPatient.id} variant="full" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
